import { organizationsPath } from '@app/constants/url';
import { UserEntity } from '@data';
import React, { ReactElement, useEffect, useMemo } from 'react';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useStore } from '@data/storages';
import { useOrganizations } from '@data/hooks/organizations';
import { Roles } from '@app/utils/types';
import OnboardingDialog from '@app/components/core/philBot/OnboardingDialog';
import Organizations from './organizations';

export interface AppProps extends React.HTMLAttributes<HTMLDivElement> {
  user: UserEntity;
}

const App = ({ user }: AppProps): ReactElement => {
  const { organization, setOrganization } = useStore();
  const { data: organizations } = useOrganizations(user?.id);
  const location = useLocation();

  const onOrgScopedRoute = useMemo(
    () => Boolean(matchPath({ path: `${organizationsPath}/:organizationId`, end: false }, location.pathname)),
    [location.pathname],
  );

  useEffect(() => {
    if (onOrgScopedRoute) {
      return;
    }
    if (!organization && organizations?.items.length) {
      setOrganization(organizations.items[0]);
    }
  }, [onOrgScopedRoute, organization, organizations, setOrganization]);

  // Mounted here rather than in the layout, so the `core` barrel does not drag the wizard's data hooks
  // into every module that imports a core component — which is most of the app's tests.
  const isEmployee = organizations?.items?.[0]?.role !== Roles.client;

  return (
    <>
      {isEmployee && <OnboardingDialog userId={user?.id} />}
      <Routes>
        <Route path={`${organizationsPath}/*`} element={<Organizations user={user} />} />
        <Route path="*" element={<Navigate to={organizationsPath} replace />} />
      </Routes>
    </>
  );
};

export default App;
